import HttpClient from '../HttpClient';

export enum CommunicationChannel {
    SMS = 'SMS',
    WHATSAPP = 'WHATSAPP',
    FCM = 'FCM',
}

export enum RecipientType {
    CUSTOMER = 'CUSTOMER',
    COOK = 'COOK',
}

export interface TemplateVariant {
    id: number;
    language: string;
    templateText: string;
    variables: string[];
    buttons?: string[];   // predefined interactive buttons (WhatsApp only), display-only
    defaultVariables?: Record<string, string>; // pre-filled values from template content (FCM)
}

export interface Template {
    id: number;
    name: string;
    friendlyName: string | null;
    sampleMessage: string | null;
    priority?: string;
    type?: string;
    variants: TemplateVariant[];
}

export interface PreviewResponse {
    preview: string;
}

export interface SendResponse {
    success: boolean;
}

export interface CommunicationHistoryItem {
    id: number;
    channel: CommunicationChannel;
    templateName: string;
    message: string;
    status: string;
    sentAt: string;
}

export interface CommunicationHistoryFilters {
    channel?: CommunicationChannel;
    dateFrom?: string;   // ISO date string
    dateTo?: string;     // ISO date string
    status?: string;
}

export interface PaginatedCommunicationHistory {
    data: CommunicationHistoryItem[];
    count: number;
    next: number;
}

export default class CommunicationManagementService {
    async listTemplates(channel: CommunicationChannel): Promise<Template[]> {
        const res = await HttpClient.get('/centaurus/communication/templates', {
            params: { channel },
        });
        return res.data;
    }

    async previewMessage(
        channel: CommunicationChannel,
        templateVariantId: number,
        variables: Record<string, string>
    ): Promise<PreviewResponse> {
        const res = await HttpClient.post('/centaurus/communication/preview', {
            channel,
            templateVariantId,
            variables,
        });
        return res.data;
    }

    async getCustomerCommunicationHistory(
        customerId: number,
        filters: CommunicationHistoryFilters = {},
        limit: number = 10,
        offset: number = 0,
    ): Promise<PaginatedCommunicationHistory> {
        const res = await HttpClient.get(`/centaurus/communication/customer/${customerId}/history`, {
            params: { ...filters, limit, offset },
        });
        return res.data;
    }

    async sendMessage(
        channel: CommunicationChannel,
        templateVariantId: number,
        variables: Record<string, string>,
        recipientType: RecipientType,
        recipientId: number
    ): Promise<SendResponse> {
        const res = await HttpClient.post('/centaurus/communication/send', {
            channel,
            templateVariantId,
            variables,
            recipientType,
            recipientId,
        });
        return res.data;
    }
}
